import {Component, ChangeDetectionStrategy, input} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

/** Shape of `response.data.mfa` from GET /api/v1/auth/policy/effective. */
export interface MfaNudgeAdvisory {
  enforcementScheduled?: boolean;
  enforceAt?: string | null;
  daysRemaining?: number | null;
  dismissible?: boolean;
  enrolled?: boolean;
  ssoExempt?: boolean;
  graceUntil?: string | null;
  /** False while a week's postponement is still running, so the popup stays away. */
  reminderDue?: boolean;
  reminderDeferredUntil?: string | null;
  /** Whether a postponement is still available to this account. */
  reminderDeferrable?: boolean;
  /** How many postponements are left, for the wording on the button's sibling text. */
  reminderDeferralsLeft?: number;
  /** Enforcement reaches this person now, with or without an announced date. */
  enforcementDue?: boolean;
  reminderDeferralDays?: number;
}

export type MfaNudgeResult = 'enable' | 'defer';

/** localStorage key holding the local calendar day (YYYY-MM-DD) the nudge was last deferred. */
export const MFA_NUDGE_DISMISSED_KEY = 'mfaNudgeDismissedOn';

/** Local (not UTC) calendar day as YYYY-MM-DD, so "once per day" follows the user's clock. */
export function localDateKey(date: Date = new Date()): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** The nudge is due unless it was already deferred today. */
export function isMfaNudgeDue(dismissedOn: string | null | undefined, today: Date = new Date()): boolean {
  return !dismissedOn || dismissedOn !== localDateKey(today);
}

/**
 * Whether the inline enforcement banner concerns this person at all. `enforcementDue` is
 * computed from the policy alone - with two-factor required by default it is true for
 * everyone - so enrolment and the SSO exemption have to be checked here, the same way the
 * route guard does.
 */
export function isMfaAdvisoryDue(advisory: MfaNudgeAdvisory | null | undefined): boolean {
  if (!advisory || advisory.enrolled || advisory.ssoExempt) {
    return false;
  }
  return Boolean(advisory.enforcementScheduled || advisory.enforcementDue);
}

/**
 * What a dismissal of the banner is remembered against: the deadline it was counting down
 * to. A later postponement or a newly announced date is a new deadline and shows it again.
 * With no date at all every such user shared the empty string, so one dismissal was final.
 */
export function mfaAdvisoryDismissKey(advisory: MfaNudgeAdvisory | null | undefined): string {
  return String(advisory?.graceUntil ?? advisory?.enforceAt ?? 'now');
}

@Component({
  selector: 'app-mfa-nudge-modal',
  templateUrl: './mfa-nudge-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule]
})
export class MfaNudgeModalComponent {
  readonly advisory = input<MfaNudgeAdvisory | null>(null);

  // Assigned by SbModalHostComponent when opened through SbModalService.
  closeModal: (result?: MfaNudgeResult) => void = () => undefined;
  dismissModal: (reason?: string) => void = () => undefined;

  get enforceDate(): string {
    const raw = this.advisory()?.enforceAt;
    if (!raw) {
      return '';
    }
    const value = new Date(raw);
    return Number.isFinite(value.getTime()) ? value.toLocaleDateString() : String(raw);
  }

  /**
   * Whether there is an announced date to count down to. Two-factor is required by
   * default and usually has none, in which case the body must not read 'becomes
   * mandatory on  (0 day(s) left)'.
   */
  get hasDeadline(): boolean {
    const advisory = this.advisory();
    return Boolean(advisory?.enforceAt) && Number(advisory?.daysRemaining ?? 0) > 0;
  }

  get daysRemaining(): number {
    return Math.max(0, Number(this.advisory()?.daysRemaining ?? 0));
  }

  /** The server withdraws the "later" option inside the final days before enforcement. */
  get dismissible(): boolean {
    return this.advisory()?.dismissible !== false;
  }

  enableNow(): void {
    this.closeModal('enable');
  }

  remindLater(): void {
    this.dismissModal('later');
  }

  /**
   * The one-week postponement, which the server records so it cannot be taken twice.
   * Distinct from "remind me later": that is a local, per-day quiet, this is a single
   * deliberate choice the account keeps a record of.
   */
  get deferrable(): boolean {
    return this.dismissible && this.advisory()?.reminderDeferrable === true;
  }

  get deferralDays(): number {
    return Math.max(1, Number(this.advisory()?.reminderDeferralDays ?? 7));
  }

  deferForAWeek(): void {
    this.closeModal('defer');
  }
}
